import { useState, useEffect } from 'react';
import { esignAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Save, Plus, Trash2, PenTool, FileText, User, Building, DollarSign, MoveUp, MoveDown } from 'lucide-react';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
  { value: 'textarea', label: 'Long Text' },
];

const SECTIONS = [
  { value: 'personal', label: "Applicant's Personal Information", icon: User },
  { value: 'corporate', label: 'Corporate Information', icon: Building },
  { value: 'net_worth', label: 'Personal Net Worth', icon: DollarSign },
];

const PREFILL_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'name', label: 'Client Name' },
  { value: 'email', label: 'Client Email' },
  { value: 'phone', label: 'Client Phone' },
  { value: 'company', label: 'Client Company' },
];

export default function CreditAppTemplate() {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await esignAPI.getTemplate();
        setTemplate(res.data);
      } catch { toast.error('Failed to load template'); }
      setLoading(false);
    })();
  }, []);

  const fields = template?.fields || [];

  const updateField = (idx, key, val) => {
    setTemplate(t => {
      const f = [...t.fields];
      f[idx] = { ...f[idx], [key]: val };
      return { ...t, fields: f };
    });
  };

  const addField = (section) => {
    const id = `custom_${Date.now()}`;
    setTemplate(t => ({
      ...t,
      fields: [...t.fields, { id, label: 'New Field', type: 'text', required: false, section: section || 'personal', width: 'half', prefill_key: '' }]
    }));
  };

  const removeField = (idx) => {
    setTemplate(t => ({ ...t, fields: t.fields.filter((_, i) => i !== idx) }));
  };

  const moveField = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= fields.length) return;
    setTemplate(t => {
      const f = [...t.fields];
      [f[idx], f[newIdx]] = [f[newIdx], f[idx]];
      return { ...t, fields: f };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await esignAPI.updateTemplate(template);
      toast.success('Credit app template saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  if (loading) return <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />;

  const grouped = SECTIONS.map(s => ({
    ...s,
    fields: fields.map((f, i) => ({ ...f, _idx: i })).filter(f => f.section === s.value),
  }));

  return (
    <div data-testid="credit-app-template-page" className="max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <PenTool className="w-5 h-5 text-[#E31937]" /> Credit Application Template
          </h1>
          <p className="text-sm text-gray-500 mt-1">Design the credit application form clients will fill out and sign</p>
        </div>
        <Button className="bg-[#E31937] hover:bg-[#C4122C] text-white" onClick={handleSave} disabled={saving} data-testid="save-template-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Template'}
        </Button>
      </div>

      {/* Template Settings */}
      <Card className="border-gray-200 mb-6">
        <CardContent className="p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 uppercase">Application Title</label>
              <Input value={template?.title || ''} onChange={e => setTemplate(t => ({ ...t, title: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" data-testid="template-title" />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase">Instructions</label>
              <Input value={template?.description || ''} onChange={e => setTemplate(t => ({ ...t, description: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" data-testid="template-description" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase">Authorization / Legal Text</label>
            <Textarea value={template?.authorization_text || ''} onChange={e => setTemplate(t => ({ ...t, authorization_text: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1 text-xs" rows={4} data-testid="template-authorization" />
          </div>
        </CardContent>
      </Card>

      {/* Fields by Section */}
      {grouped.map(section => {
        const SectionIcon = section.icon;
        return (
          <Card key={section.value} className="border-gray-200 mb-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <SectionIcon className="w-4 h-4 text-[#E31937]" />
                {section.label}
                <Badge variant="outline" className="text-[10px] ml-auto">{section.fields.length} fields</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {section.fields.length === 0 && (
                <p className="text-xs text-gray-400 italic py-2">No fields in this section</p>
              )}
              {section.fields.map(field => (
                <div key={field._idx} className="flex items-center gap-1.5 p-2 bg-gray-50 rounded-lg border border-gray-100 group" data-testid={`field-row-${field.id}`}>
                  <div className="flex flex-col gap-0 flex-shrink-0">
                    <button onClick={() => moveField(field._idx, -1)} className="text-gray-300 hover:text-gray-500 p-0"><MoveUp className="w-3 h-3" /></button>
                    <button onClick={() => moveField(field._idx, 1)} className="text-gray-300 hover:text-gray-500 p-0"><MoveDown className="w-3 h-3" /></button>
                  </div>
                  <Input value={field.label} onChange={e => updateField(field._idx, 'label', e.target.value)} className="text-xs bg-white border-gray-200 h-7 flex-[3]" />
                  <Select value={field.type} onValueChange={v => updateField(field._idx, 'type', v)}>
                    <SelectTrigger className="text-xs bg-white border-gray-200 h-7 flex-[1.5]"><SelectValue /></SelectTrigger>
                    <SelectContent>{FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={field.prefill_key || ''} onValueChange={v => updateField(field._idx, 'prefill_key', v === 'none' ? '' : v)}>
                    <SelectTrigger className="text-xs bg-white border-gray-200 h-7 flex-[1.5]"><SelectValue placeholder="Prefill" /></SelectTrigger>
                    <SelectContent>{PREFILL_OPTIONS.map(o => <SelectItem key={o.value || 'none'} value={o.value || 'none'}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Switch checked={field.required} onCheckedChange={v => updateField(field._idx, 'required', v)} className="scale-75" />
                    <span className="text-[9px] text-gray-400 w-5">Req</span>
                  </div>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 flex-shrink-0" onClick={() => removeField(field._idx)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="w-full border-dashed border-gray-300 text-gray-400 hover:text-[#E31937] hover:border-[#E31937] text-xs h-7 mt-2" onClick={() => addField(section.value)}>
                <Plus className="w-3 h-3 mr-1" /> Add Field to {section.label}
              </Button>
            </CardContent>
          </Card>
        );
      })}

      {/* Preview */}
      <Card className="border-gray-200 mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#E31937]" /> Form Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mx-auto bg-white border border-gray-200 rounded-lg p-6 max-w-3xl">
            {/* CTS Header */}
            <div className="bg-[#E31937] text-white text-center py-4 rounded-t-lg -mt-6 -mx-6 mb-4">
              <h2 className="text-lg font-bold tracking-wide">CANADIAN TRUCK SALES</h2>
              <p className="text-xs opacity-80">5939 Shawson Dr Suite #100, Mississauga, ON L4W 3Y2 | (416) 417-2525</p>
            </div>
            <h3 className="text-base font-bold text-gray-900 text-center mb-1">{template?.title || 'Credit Application'}</h3>
            <p className="text-[10px] text-gray-500 text-center mb-4">{template?.description}</p>

            {grouped.map(section => section.fields.length > 0 && (
              <div key={section.value} className="mb-5">
                <div className="bg-gray-100 px-3 py-1.5 rounded text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                  {section.label}
                </div>
                <div className="grid grid-cols-6 gap-x-2 gap-y-1.5 px-1">
                  {section.fields.map(f => {
                    const w = f.width || 'half';
                    const span = w === 'full' ? 'col-span-6' : w === 'half' ? 'col-span-3' : w === 'third' ? 'col-span-2' : w === 'quarter' ? 'col-span-3 md:col-span-3 lg:col-span-2' : 'col-span-1';
                    return (
                      <div key={f._idx} className={span}>
                        <label className="text-[9px] text-gray-500">{f.label} {f.required && <span className="text-red-500">*</span>}</label>
                        <div className="h-6 bg-gray-50 border border-gray-200 rounded text-[9px] text-gray-300 flex items-center px-1.5 mt-0.5">
                          {f.prefill_key ? `[${f.prefill_key}]` : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Authorization text preview */}
            <div className="mt-4 px-1">
              <p className="text-[8px] text-gray-500 leading-relaxed">{template?.authorization_text?.substring(0, 200)}...</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 px-1">
              <div>
                <p className="text-[9px] text-gray-500 mb-1">Applicant Signature</p>
                <div className="border-b border-gray-300 h-6" />
                <p className="text-[8px] text-gray-400 mt-0.5">Date: _______________</p>
              </div>
              <div>
                <p className="text-[9px] text-gray-500 mb-1">Co-Applicant Signature</p>
                <div className="border-b border-gray-300 h-6" />
                <p className="text-[8px] text-gray-400 mt-0.5">Date: _______________</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
